/**
 * C.0 + Batch 2 Part B - Admin Maintenance Runner
 *
 * Whitelisted operation dispatcher. Every mutation supports a Preview
 * step (dry-run under the hood) followed by an explicit Apply step
 * gated behind a confirm dialog that summarizes the Preview result
 * (if one was run in this session).
 *
 * Zero em/en-dashes. Ember/navy palette. No new palette invented.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Play, Eye, ShieldAlert, Clock, CheckCircle2, XCircle, Sparkles, ChevronDown, ChevronRight } from 'lucide-react';
import api from '@/utils/api';

const DANGER_COLOR = {
  safe: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  moderate: 'bg-amber-50 text-amber-800 border-amber-200',
  caution: 'bg-red-50 text-red-800 border-red-200',
};

// ---------------------------------------------------------------------------
// OpCard - one card per whitelisted operation. Preview + Apply buttons.
// ---------------------------------------------------------------------------
const OpCard = ({ op, onPreview, onApply, busyOp, lastPreview }) => {
  const [params, setParams] = useState({});
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isBusyThis = busyOp === op.key;
  const needsReason = op.danger !== 'safe';
  const paramSummary = Object.entries(params)
    .filter(([, v]) => (v || '').toString().trim() !== '')
    .map(([k, v]) => `${k}=${v}`).join(', ');

  const submitPreview = () => {
    onPreview(op, { ...params });
  };

  const submitApply = () => {
    if (needsReason && reason.trim().length < 5) {
      toast.error('Please write a reason (min 5 chars) before applying.');
      return;
    }
    setConfirmOpen(true);
  };

  const confirmApply = () => {
    setConfirmOpen(false);
    onApply(op, { ...params }, reason.trim());
  };

  const previewSummary = lastPreview ? summarizePreview(lastPreview) : null;

  return (
    <Card data-testid={`admin-maintenance-op-${op.key}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-sm font-semibold text-primary-900">
            {op.key}
          </CardTitle>
          <Badge variant="outline" className={DANGER_COLOR[op.danger] || ''}>
            {op.danger}
          </Badge>
        </div>
        <p className="text-xs text-neutral-600 mt-1">{op.description}</p>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {(op.required_params || []).map((p) => (
          <div key={p}>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">{p}</label>
            <Input
              value={params[p] || ''}
              onChange={(e) => setParams((s) => ({ ...s, [p]: e.target.value }))}
              placeholder={p}
              data-testid={`admin-maintenance-op-${op.key}-param-${p}`}
              className="mt-1"
            />
          </div>
        ))}
        {needsReason && (
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Reason (5+ chars, required to Apply)"
            data-testid={`admin-maintenance-op-${op.key}-reason`}
          />
        )}
        {op.supports_dry_run !== false ? (
          <div className="grid grid-cols-2 gap-2 pt-1">
            <Button
              onClick={submitPreview}
              disabled={isBusyThis}
              variant="outline"
              className="w-full text-secondary-800 border-secondary-300 hover:bg-secondary-50"
              data-testid={`admin-maintenance-op-${op.key}-preview-btn`}
            >
              <Eye className="h-3.5 w-3.5 mr-1" />
              {isBusyThis && busyOp === op.key ? 'Running...' : 'Preview'}
            </Button>
            <Button
              onClick={submitApply}
              disabled={isBusyThis}
              className="w-full bg-primary-800 hover:bg-primary-900 text-white"
              data-testid={`admin-maintenance-op-${op.key}-apply-btn`}
            >
              <Play className="h-3.5 w-3.5 mr-1" />
              Apply
            </Button>
          </div>
        ) : (
          <Button
            onClick={submitApply}
            disabled={isBusyThis}
            className="w-full bg-primary-800 hover:bg-primary-900 text-white"
            data-testid={`admin-maintenance-op-${op.key}-run-btn`}
          >
            <Play className="h-3.5 w-3.5 mr-1" /> Run
          </Button>
        )}
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid={`admin-maintenance-op-${op.key}-confirm-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-accent-700" />
              Apply {op.key}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-neutral-700">
                <div>{op.description}</div>
                {paramSummary && (
                  <div className="text-xs font-mono bg-neutral-50 border border-neutral-200 rounded p-2">
                    params: {paramSummary}
                  </div>
                )}
                {previewSummary ? (
                  <div className="rounded border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900">
                    <div className="font-semibold mb-1">Last Preview in this session:</div>
                    {previewSummary}
                  </div>
                ) : (
                  <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                    You have not run Preview for this operation yet. Applying without a Preview is only recommended for read-safe operations.
                  </div>
                )}
                {op.danger !== 'safe' && (
                  <div className="text-xs text-accent-800">
                    Danger level: <strong>{op.danger}</strong>. This action is audited with your reason: <em>{reason.trim() || '(none)'}</em>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`admin-maintenance-op-${op.key}-confirm-cancel`}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmApply}
              className="bg-primary-800 hover:bg-primary-900 text-white"
              data-testid={`admin-maintenance-op-${op.key}-confirm-apply`}
            >
              Apply now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

// Extract a short human summary from a maintenance run response so the
// confirm dialog can show it.
function summarizePreview(res) {
  if (!res) return null;
  const meta = res.response || {};
  const affected = meta.affected ?? meta.count ?? meta.would_affect ?? meta.updated ?? null;
  const elapsed = res.elapsed_ms ?? meta.elapsed_ms ?? null;
  if (affected == null && elapsed == null) {
    return <span>Preview completed (status: <strong>{res.status}</strong>). See Recent runs for full details.</span>;
  }
  return (
    <span>
      Preview reported {affected != null ? <><strong>{affected}</strong> row(s) affected</> : 'no row count'}
      {elapsed != null && <> in <strong>{elapsed}ms</strong></>}. Status <strong>{res.status}</strong>.
    </span>
  );
}

// Bundle 30 - reusable per-field chip list. Pulled out so both the
// results panel below the shortcut card AND the Apply confirmation
// modal can render the same breakdown.
const PerFieldChips = ({ preview, testidPrefix }) => {
  const resp = (preview && preview.response) || {};
  const meta = resp.summary || resp || {};
  const perField = meta.fields_written || {};
  const entries = Object.entries(perField).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testidPrefix ? `${testidPrefix}-list` : undefined}>
      {entries.map(([k, n]) => (
        <span
          key={k}
          className="inline-flex items-center rounded-full border border-emerald-200 bg-white text-emerald-900 font-semibold px-2 py-0.5 text-[11px]"
          data-testid={testidPrefix ? `${testidPrefix}-${k}` : undefined}
        >
          {k}: <span className="ml-1 tabular-nums">{n}</span>
        </span>
      ))}
    </div>
  );
};

// Bundle 29a - render the weak-hydrate preview response as per-field
// count chips plus a small sample-before-after list. Preview panel is
// only shown after a Preview run in the current session.
const WeakHydrateResults = ({ preview }) => {
  const resp = (preview && preview.response) || {};
  const meta = resp.summary || resp || {};
  const scanned = meta.candidates_scanned ?? meta.scanned ?? 0;
  const updated = meta.hydrated ?? meta.updated ?? 0;
  const wouldUpdate = meta.would_update ?? updated;
  const perField = meta.fields_written || {};
  const perFieldEntries = Object.entries(perField).sort((a, b) => b[1] - a[1]);
  const sample = meta.sample_before_after || meta.sample
                  || (resp.details ? resp.details.slice(0, 10) : []);
  return (
    <div className="rounded border border-rose-100 bg-rose-50/40 p-3 text-xs text-rose-900 space-y-2">
      <div className="flex flex-wrap gap-3 items-baseline">
        <span><strong className="tabular-nums">{scanned}</strong> scanned</span>
        <span><strong className="tabular-nums">{preview?.dry_run ? wouldUpdate : updated}</strong> {preview?.dry_run ? 'would update' : 'updated'}</span>
        {preview?.elapsed_ms != null && <span>in <strong className="tabular-nums">{preview.elapsed_ms}ms</strong></span>}
      </div>
      {perFieldEntries.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="admin-maintenance-weak-hydrate-per-field-chips">
          {perFieldEntries.map(([k, n]) => (
            <span
              key={k}
              className="inline-flex items-center rounded-full border border-rose-200 bg-white text-rose-900 font-semibold px-2 py-0.5"
              data-testid={`admin-maintenance-weak-hydrate-chip-${k}`}
            >
              {k}: <span className="ml-1 tabular-nums">{n}</span>
            </span>
          ))}
        </div>
      )}
      {sample.length > 0 && (
        <details>
          <summary className="cursor-pointer text-[11px] font-semibold">
            Sample before/after ({sample.length})
          </summary>
          <pre className="mt-1 max-h-60 overflow-auto text-[10px] bg-white border border-rose-100 rounded p-2">
            {JSON.stringify(sample, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
};

const AdminMaintenancePage = () => {
  const [ops, setOps] = useState([]);
  const [runs, setRuns] = useState([]);
  const [busyOp, setBusyOp] = useState('');
  const [expanded, setExpanded] = useState(null);
  // Session-scoped: last Preview result per op.key -> run summary.
  const [previewByOp, setPreviewByOp] = useState({});

  const loadOps = useCallback(() => {
    api.get('/admin/maintenance/ops').then((r) => setOps(r.data.ops || [])).catch(() => {});
  }, []);
  const loadRuns = useCallback(() => {
    api.get('/admin/maintenance/runs', { params: { limit: 50 } })
      .then((r) => setRuns(r.data.items || []))
      .catch(() => {});
  }, []);
  useEffect(() => { loadOps(); loadRuns(); }, [loadOps, loadRuns]);

  const runOp = async (payload) => {
    setBusyOp(payload.operation);
    try {
      const r = await api.post('/admin/maintenance/run', payload);
      toast.success(`${payload.operation} ${r.data.dry_run ? '(preview)' : ''} ${r.data.status}`);
      loadRuns();
      return r.data;
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.error || 'Run failed');
      return null;
    } finally {
      setBusyOp('');
    }
  };

  const onPreview = async (op, params) => {
    const payload = { operation: op.key, params: { ...params, dry_run: true } };
    const result = await runOp(payload);
    if (result) {
      setPreviewByOp((cur) => ({ ...cur, [op.key]: result }));
    }
  };
  const onApply = async (op, params, reason) => {
    const payload = {
      operation: op.key,
      params: { ...params, dry_run: false },
      reason: reason || undefined,
    };
    await runOp(payload);
  };

  const openDetail = async (id) => {
    if (expanded?.id === id) { setExpanded(null); return; }
    try {
      const r = await api.get(`/admin/maintenance/runs/${id}`);
      setExpanded({ id, ...r.data });
    } catch { /* ignore */ }
  };

  const hydrateOp = ops.find((o) => o.key === 'backfill-rsvp-hydration');
  const shortcutBusy = busyOp === 'backfill-rsvp-hydration';
  const [hydrateConfirm, setHydrateConfirm] = useState(false);

  // Bundle 29a shortcut - same UX as the RSVP hydrator but forwards to
  // the weak-value-overwrite variant. Silent overwrite of "D U" style
  // weak names, empty city/state, etc.
  const weakOp = ops.find((o) => o.key === 'hydrate-weak-profiles');
  const weakBusy = busyOp === 'hydrate-weak-profiles';
  const [weakConfirm, setWeakConfirm] = useState(false);

  return (
    <div className="p-6 space-y-6" data-testid="admin-maintenance-page">
      {typeof window !== 'undefined' && window.location.pathname.includes('/advanced-tools') ? (
        <div
          data-testid="advanced-tools-warning"
          className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-sm"
        >
          <strong>Advanced diagnostics.</strong> Most fixes are on{' '}
          <a href="/admin/health" className="underline font-semibold">
            Data Health
          </a>
          {' '}now. Use this page only when a card there does not exist for what you need.
        </div>
      ) : null}
      <h1 className="font-display text-2xl font-bold text-primary-900">Maintenance</h1>

      {hydrateOp && (
        <Card
          className="border-secondary-200 bg-gradient-to-r from-secondary-50 to-white"
          data-testid="admin-maintenance-hydrate-shortcut"
        >
          <CardContent className="p-4 flex items-center gap-4 flex-wrap">
            <Sparkles className="h-5 w-5 text-secondary-700 shrink-0" />
            <div className="flex-1 min-w-[200px]">
              <div className="font-semibold text-primary-900">Hydrate all users from RSVP</div>
              <div className="text-xs text-neutral-600">
                Retro-copy RSVP answers into user.profile fields. Idempotent, safe to re-run.
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => onPreview(hydrateOp, {})}
              disabled={shortcutBusy}
              className="text-secondary-800 border-secondary-300 hover:bg-secondary-50"
              data-testid="admin-maintenance-hydrate-preview-btn"
            >
              <Eye className="h-3.5 w-3.5 mr-1" /> Preview
            </Button>
            <Button
              onClick={() => setHydrateConfirm(true)}
              disabled={shortcutBusy || !previewByOp['backfill-rsvp-hydration']}
              className="bg-primary-800 hover:bg-primary-900 text-white disabled:opacity-50"
              data-testid="admin-maintenance-hydrate-apply-btn"
              title={!previewByOp['backfill-rsvp-hydration'] ? 'Run Preview first' : 'Apply hydration'}
            >
              <Play className="h-3.5 w-3.5 mr-1" /> Apply
            </Button>
          </CardContent>
          <AlertDialog open={hydrateConfirm} onOpenChange={setHydrateConfirm}>
            <AlertDialogContent data-testid="admin-maintenance-hydrate-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Apply RSVP hydration to all users?</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2 text-sm text-neutral-700">
                    <div>
                      This will copy RSVP answers into every matching user profile.
                      The operation is idempotent and safe to re-run.
                    </div>
                    {previewByOp['backfill-rsvp-hydration']
                      ? (
                        <div className="rounded border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900 space-y-1.5" data-testid="admin-maintenance-hydrate-modal-summary">
                          <div className="font-semibold">Last Preview in this session:</div>
                          <div>{summarizePreview(previewByOp['backfill-rsvp-hydration'])}</div>
                          <PerFieldChips
                            preview={previewByOp['backfill-rsvp-hydration']}
                            testidPrefix="admin-maintenance-hydrate-modal-chip"
                          />
                        </div>
                      )
                      : (
                        <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900" data-testid="admin-maintenance-hydrate-modal-no-preview">
                          Run Preview first to see per-field breakdown.
                        </div>
                      )}
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    setHydrateConfirm(false);
                    onApply(hydrateOp, {}, 'apply RSVP hydration to all users');
                  }}
                  className="bg-primary-800 hover:bg-primary-900 text-white"
                >
                  Apply now
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </Card>
      )}

      {weakOp && (
        <Card
          className="border-rose-200 bg-gradient-to-r from-rose-50 to-white"
          data-testid="admin-maintenance-weak-hydrate-shortcut"
        >
          <CardContent className="p-4 flex items-center gap-4 flex-wrap">
            <Sparkles className="h-5 w-5 text-rose-700 shrink-0" />
            <div className="flex-1 min-w-[200px]">
              <div className="font-semibold text-primary-900">Hydrate weak profiles</div>
              <div className="text-xs text-neutral-600">
                Fix "D U" style users: silently overwrite first_name/last_name under 3 chars and
                empty city/state with the values from their RSVP answers. Idempotent.
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => onPreview(weakOp, {})}
              disabled={weakBusy}
              className="text-rose-800 border-rose-300 hover:bg-rose-50"
              data-testid="admin-maintenance-weak-hydrate-preview-btn"
            >
              <Eye className="h-3.5 w-3.5 mr-1" /> Preview
            </Button>
            <Button
              onClick={() => setWeakConfirm(true)}
              disabled={weakBusy || !previewByOp['hydrate-weak-profiles']}
              className="bg-primary-800 hover:bg-primary-900 text-white disabled:opacity-50"
              data-testid="admin-maintenance-weak-hydrate-apply-btn"
              title={!previewByOp['hydrate-weak-profiles'] ? 'Run Preview first' : 'Apply hydration'}
            >
              <Play className="h-3.5 w-3.5 mr-1" /> Apply
            </Button>
          </CardContent>
          {previewByOp['hydrate-weak-profiles'] && (
            <CardContent className="p-4 pt-0" data-testid="admin-maintenance-weak-hydrate-results">
              <WeakHydrateResults preview={previewByOp['hydrate-weak-profiles']} />
            </CardContent>
          )}
          <AlertDialog open={weakConfirm} onOpenChange={setWeakConfirm}>
            <AlertDialogContent data-testid="admin-maintenance-weak-hydrate-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Apply weak-value hydration to all users?</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2 text-sm text-neutral-700">
                    <div>
                      This will SILENTLY overwrite weak first_name / last_name (under 3 chars)
                      and empty city / state with the values pulled from each user's RSVP answers.
                      Strong existing values ({'>='}3 chars) are preserved. No user email is sent.
                      Every hydration writes a system:hydrator audit entry.
                    </div>
                    {previewByOp['hydrate-weak-profiles']
                      ? (
                        <div className="rounded border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900 space-y-1.5" data-testid="admin-maintenance-weak-hydrate-modal-summary">
                          <div className="font-semibold">Last Preview in this session:</div>
                          <div>{summarizePreview(previewByOp['hydrate-weak-profiles'])}</div>
                          <PerFieldChips
                            preview={previewByOp['hydrate-weak-profiles']}
                            testidPrefix="admin-maintenance-weak-hydrate-modal-chip"
                          />
                        </div>
                      )
                      : (
                        <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900" data-testid="admin-maintenance-weak-hydrate-modal-no-preview">
                          Run Preview first to see per-field breakdown.
                        </div>
                      )}
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    setWeakConfirm(false);
                    onApply(weakOp, {}, 'apply weak-value hydration to all users');
                  }}
                  className="bg-primary-800 hover:bg-primary-900 text-white"
                >
                  Apply now
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h2 className="text-sm uppercase tracking-widest text-neutral-500 mb-3">Operations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {ops.map((op) => (
              <OpCard
                key={op.key}
                op={op}
                onPreview={onPreview}
                onApply={onApply}
                busyOp={busyOp}
                lastPreview={previewByOp[op.key]}
              />
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-sm uppercase tracking-widest text-neutral-500 mb-3">Recent runs</h2>
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm" data-testid="admin-maintenance-runs-table">
                <thead className="text-left text-xs uppercase tracking-wider text-neutral-500">
                  <tr>
                    <th className="py-2 px-3">When</th>
                    <th className="py-2 px-3">Op</th>
                    <th className="py-2 px-3">Preview</th>
                    <th className="py-2 px-3">Status</th>
                    <th className="py-2 px-3">Summary</th>
                    <th className="py-2 px-3 text-right">ms</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <React.Fragment key={r.id}>
                      <tr
                        className="border-t border-neutral-100 cursor-pointer hover:bg-neutral-50"
                        onClick={() => openDetail(r.id)}
                        data-testid={`admin-maintenance-run-row-${r.id}`}
                      >
                        <td className="py-2 px-3 font-mono text-xs text-neutral-500">
                          {(r.started_at || '').slice(11, 19)}
                        </td>
                        <td className="py-2 px-3">{r.operation}</td>
                        <td className="py-2 px-3">
                          {r.dry_run ? <Clock className="h-3 w-3 text-amber-600" /> : ''}
                        </td>
                        <td className="py-2 px-3">
                          {r.status === 'success'
                            ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                            : <XCircle className="h-4 w-4 text-red-600" />}
                        </td>
                        <td className="py-2 px-3">
                          <RunSummaryCell run={r} />
                        </td>
                        <td className="py-2 px-3 text-right font-mono">{r.elapsed_ms}</td>
                      </tr>
                      {expanded?.id === r.id && (
                        <tr data-testid={`admin-maintenance-run-detail-${r.id}`}>
                          <td colSpan={6} className="bg-neutral-50 p-3">
                            <pre className="text-[11px] font-mono overflow-x-auto whitespace-pre-wrap">
                              {JSON.stringify(expanded.response, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {runs.length === 0 && (
                    <tr><td colSpan={6} className="p-3 text-neutral-500 text-sm">No runs yet.</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminMaintenancePage;

/* ------------------------------------------------------------------ */
/*  Iter22 - surface counters + fields_written on every Recent Runs   */
/*  row so admins never mistake `status=success` + zero writes for a  */
/*  successful backfill. Silent-no-op is the failure mode we've been  */
/*  fighting; make it visible.                                        */
/* ------------------------------------------------------------------ */

const HEADLINE_KEYS = [
  ["linked_count", "linked"],
  ["hydrated", "hydrated"],
  ["candidates_scanned", "scanned"],
  ["would_link", "would_link"],
  ["would_delete", "would_delete"],
  ["deleted", "deleted"],
  ["reconciled", "reconciled"],
  ["empty_with_rsvp_data", "empty_profiles"],
  ["skipped_already_filled", "skipped"],
];

const RunSummaryCell = ({ run }) => {
  const [open, setOpen] = useState(false);
  const summary = (run && run.response_summary) || {};
  const fw = summary.fields_written || {};
  const fwEntries = Object.entries(fw).sort((a, b) => b[1] - a[1]);
  const totalFields = fwEntries.reduce((n, [, v]) => n + v, 0);
  const headline = HEADLINE_KEYS
    .filter(([k]) => summary[k] != null)
    .map(([k, label]) => `${label}: ${summary[k]}`)
    .join(' · ');
  const hasCounters = headline || fwEntries.length > 0;
  if (!hasCounters) {
    return (
      <span className="text-[10px] text-neutral-400 italic">no writes</span>
    );
  }
  return (
    <div className="min-w-0">
      {headline && (
        <div className="text-[11px] text-primary-900 whitespace-nowrap overflow-hidden text-ellipsis max-w-[280px]"
             title={headline}>
          {headline}
        </div>
      )}
      {fwEntries.length > 0 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
          className="mt-0.5 inline-flex items-center gap-1 rounded-full border border-secondary-200 bg-secondary-50 text-secondary-900 hover:bg-secondary-100 px-2 py-0.5 text-[10px] font-semibold"
          data-testid={`admin-maintenance-fields-written-toggle-${run.id}`}
          aria-expanded={open}
        >
          {totalFields} field write{totalFields === 1 ? '' : 's'}
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>
      )}
      {open && (
        <div
          className="mt-2 rounded-md border border-neutral-200 bg-white p-2 space-y-0.5"
          data-testid={`admin-maintenance-fields-written-panel-${run.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          {fwEntries.map(([field, count]) => (
            <div key={field} className="flex items-center justify-between text-[11px] font-mono">
              <span className="text-neutral-700">{field}</span>
              <span className="text-secondary-800 font-semibold">{count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
